import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { errorClassificationLookup, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';
import omit from 'lodash-es/omit';

import {
  mapRequestQuoteToInputType,
  hasRejection,
  formattedError,
  mapRevisedQuinstreetQuoteDetails
} from 'core/helpers/quoter.service';
import { track } from 'core/helpers/analytics';
import { withRetry } from 'core/helpers/with-retry';
import {
  REQUEST_QUOTE,
  REQUEST_BRANCH_PLUS_QUOTE,
  SEND_CLUSTER_LINK,
  ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
  ON_BRANCH_PLUS_OFFER_GENERATED
} from './quote.queries';
import { GET_OFFER } from '../offer-store/offer-queries';

const ErrorType = types.model({
  type: types.maybe(types.string),
  code: types.union(types.string, types.number),
  message: types.maybe(types.string),
  data: types.maybe(types.frozen()),
  level: types.maybe(types.string),
  handleAs: types.maybe(types.string)
});

let subscription = null;

export const QuoteStore = types
  .model({
    loading: types.boolean,
    errors: types.array(ErrorType),
    prefillData: types.maybeNull(types.frozen()),
    overriddenProtectedFields: types.array(types.string),
    offer: types.maybeNull(types.frozen())
  })
  .actions((self) => ({
    requestQuinstreetQuoteAction: flow(function* requestQuinstreetQuote({
      quoteDetails,
      webUserId,
      rep,
      policyId,
      offerId,
      policyType
    }) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      try {
        const { client } = getEnv(self);
        self.loading = true;

        const parent = getParent(self);
        const customerId = policyId ? policyId.slice(0, 9) : null;
        yield parent.account.sendEventAsMember({
          eventName: 'Staff Generated QuinStreet Quote - Customer',
          properties: JSON.stringify({ webUserId, customerId, staffRep: rep.username, policyType }),
          userId: customerId || webUserId
        });

        track('Staff Generated QuinStreet Quote', { webUserId, customerId, staffRep: rep.username, policyType });

        const response = yield client.query({
          query: ASYNC_GENERATE_BRANCH_PLUS_OFFERS_FROM_OFFER,
          variables: {
            quoteDetails: mapRevisedQuinstreetQuoteDetails(quoteDetails),
            policyType,
            policyId,
            offerId,
            webUserId
          }
        });

        const quinstreetOffer = yield new Promise((resolve, reject) => {
          const observable = client.subscribe({
            query: ON_BRANCH_PLUS_OFFER_GENERATED,
            variables: { taskId: response.data.asyncGenerateBranchPlusOffersFromOffer.taskId }
          });

          subscription = observable.subscribe({
            next: ({ data }) => {
              try {
                const { onBranchPlusOfferGenerated } = data;
                if (onBranchPlusOfferGenerated.error) {
                  reject(onBranchPlusOfferGenerated.error);
                } else if (onBranchPlusOfferGenerated.data) {
                  resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                }
              } catch (err) {
                reject(err);
              } finally {
                subscription.unsubscribe();
              }
            },
            error: (err) => {
              reject(err);
            }
          });
        }).catch((err) => {
          subscription.unsubscribe();
          throw err;
        });

        if (quinstreetOffer?.branchPlusOffers?.length > 0 && quinstreetOffer.offerId) {
          let lowestPrice = Number.POSITIVE_INFINITY;
          let lowestPricedCarrier = '';
          const successfulOffers = [];

          quinstreetOffer.branchPlusOffers.forEach((offer) => {
            if (offer?.status === 'success') {
              successfulOffers.push(offer);

              if (offer?.policyType === policyType && offer?.premium && offer.premium < lowestPrice) {
                lowestPrice = offer.premium;
                lowestPricedCarrier = offer.carrierName;
              }
            } else {
              track('Staff Generated QuinStreet Quote Response Error', {
                webUserId,
                staffRep: rep.username,
                carrier: offer?.carrierName,
                customerId,
                policyType
              });
            }
          });

          successfulOffers.forEach((offer) => {
            track('Staff Generated QuinStreet Quote Response Success', {
              webUserId,
              premium: offer.premium,
              staffRep: rep.username,
              carrier: offer.carrierName,
              customerId,
              policyType,
              lowestPricedCarrier,
              lowestPrice: lowestPrice === Number.POSITIVE_INFINITY ? null : lowestPrice
            });
          });

          return quinstreetOffer;
        }
      } catch (error) {
        track('Staff Generated QuinStreet Quote Response Error', {
          webUserId,
          staffRep: rep.username,
          policyType
        });
        console.error(`Error in requestQuinstreetQuote: ${error}`);
        throw new Error(error);
      } finally {
        self.loading = false;
      }
    }),
    requestQuoteAction: flow(function* requestQuote(userInput, allowedStates, retry = true) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Quote', { userInput });

          const {
            data: { offer },
            errors
          } = yield withRetry(
            client.query({
              query: REQUEST_QUOTE,
              variables: mapRequestQuoteToInputType(userInput),
              fetchPolicy: 'no-cache'
            }),
            'requestQuote'
          );
          if (offer) {
            self.offer = offer;
          }
          if (!offer && retry && errors?.[0]?.errorType === 'ExecutionTimeout') {
            self.requestQuoteAction(userInput, allowedStates, false);
            return;
          }
          if (errors?.length) {
            self.errors = errors.map((error) => {
              try {
                const parsed = JSON.parse(error.message);
                return {
                  code: Number(parsed.code),
                  message: parsed.message
                };
              } catch (e) {
                return e;
              }
            });
            self.loading = false;
            return;
          }

          self.offer = offer;
          self.loading = false;

          if (hasRejection(offer)) {
            self.errors = offer.rejections.map(({ type, value, data, level }) => {
              return {
                type,
                code: value,
                data: data && JSON.parse(data),
                level
              };
            });
          }

          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error(`Error in requestQuote: ${error}`);
          self.loading = false;
          self.handleError(error);
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: false,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        }
      } else {
        const notLicensedError = {
          message:
            'You are not licensed in this state, if you believe this is a mistake, please contact Branch at 833-4BRANCH (833-427-2624) support@ourbranch.com'
        };
        self.loading = false;
        self.handleError(notLicensedError);
      }
    }),
    requestBranchPlusQuoteAction: flow(function* requestBranchPlusQuoteAction(userInput, allowedStates) {
      self.errors = [];
      self.overriddenProtectedFields = [];
      if (allowedStates.includes(userInput.state)) {
        // check that the user has a license for this state
        try {
          const { client } = getEnv(self);
          self.loading = true;
          track('Staff Generated Branch Plus Quote', { userInput });

          const response = yield withRetry(
            client.query({
              query: REQUEST_BRANCH_PLUS_QUOTE,
              variables: mapRequestQuoteToInputType(userInput),
              fetchPolicy: 'no-cache'
            }),
            'asyncGenerateBranchPlusOffers'
          );

          const { offerId } = yield new Promise((resolve, reject) => {
            const observable = client.subscribe({
              query: ON_BRANCH_PLUS_OFFER_GENERATED,
              variables: { taskId: response.data.BranchPlusOfferResponse.taskId }
            });

            const timeoutId = setTimeout(
              () => {
                subscription?.unsubscribe();
                reject(new Error('Branch Plus Timeout'));
              },
              5 * 60 * 1000
            );

            subscription = observable.subscribe({
              next: ({ data }) => {
                try {
                  const { onBranchPlusOfferGenerated } = data;
                  if (onBranchPlusOfferGenerated.error) {
                    clearTimeout(timeoutId);
                    reject(onBranchPlusOfferGenerated.error);
                  } else if (onBranchPlusOfferGenerated.data) {
                    clearTimeout(timeoutId);
                    resolve(JSON.parse(onBranchPlusOfferGenerated.data));
                  }
                } catch (err) {
                  clearTimeout(timeoutId);
                  reject(err);
                }
              },
              error: (err) => {
                clearTimeout(timeoutId);
                reject(err);
              }
            });
          }).catch((err) => {
            subscription.unsubscribe();
            throw err;
          });

          const offerResponse = yield client.query({
            query: GET_OFFER,
            variables: { offerId },
            fetchPolicy: 'no-cache'
          });

          if (offerResponse.data && offerResponse.data.offer) {
            const { offer } = offerResponse.data;
            self.offer = offer;

            if (hasRejection(offer)) {
              self.errors = offer.rejections.map(({ type, value, data, level }) => {
                return {
                  type,
                  code: value,
                  data: data && JSON.parse(data),
                  level
                };
              });
            }
          }

          self.loading = false;
          self.fetchAffinityData();
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: self.offer?.id,
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        } catch (error) {
          console.error(`Error in requestBranchPlusQuote: ${error}`);
          self.loading = false;
          self.handleError(error);
          track('Staff New Offer Received', {
            user: userInput.rep,
            isBranchPlus: true,
            offerId: 'No offer generated',
            requestedPolicyType: userInput.policyType,
            errors: self.errors.map((error) => error.code)
          });
        }
      } else {
        const notLicensedError = {
          message:
            'You are not licensed in this state, if you believe this is a mistake, please contact Branch at 833-4BRANCH (833-427-2624) support@ourbranch.com'
        };
        self.loading = false;
        self.handleError(notLicensedError);
      }
    }),
    sendClusterLink: flow(function* sendClusterLink(offerId, email, quote, agent) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: SEND_CLUSTER_LINK,
        variables: {
          offerId,
          revisedQuoteDetails: quote,
          email,
          agent
        }
      });
    }),
    fetchAffinityData: flow(function* fetchAffinityData(affinity) {
      if (affinity) {
        const { affinityLookups } = getParent(self);
        yield affinityLookups.getByAffinity(affinity);
      }
    }),
    handleError: (error) => {
      const parsed = formattedError(error);
      if (parsed) {
        self.errors = [...self.errors, parsed];
      }
    },
    setPrefillData(offer, skipRejectionCheck = false, preselectBundleOption = false) {
      self.overriddenProtectedFields = [];
      if (!skipRejectionCheck) {
        if (hasRejection(offer)) {
          self.errors = offer.rejections.map((rejection) => ({
            type: rejection.type,
            code: rejection.value,
            data: rejection.data && JSON.parse(rejection.data),
            level: rejection.level
          }));
        }
      }
      const prefillData = omit(offer.quote, '__typename');
      if (preselectBundleOption) {
        prefillData.selectedOption = 'HA';
      }
      self.prefillData = prefillData;
    },
    clearPrefillData() {
      self.prefillData = null;
      self.overriddenProtectedFields = [];
    },
    clearError(code) {
      self.errors = self.errors.filter((err) => err.code !== code);
    },
    setErrorToShowAsRejection(code) {
      self.errors = self.errors.map((err) => {
        if (err.code === code) {
          return { ...err, handleAs: 'REJECTION' };
        }
        return err;
      });
    },
    getAlerts(codesArray = [], policyType) {
      return self.errors.filter((error) => {
        // if this is a bypass alert and the requested policy type is not in the rejection array, dont show this error
        if (error.code && errorClassificationLookup[Number(error.code)] === 'BYPASS_ALERT' && policyType) {
          if (!rejectionTypeToPolicyTypeMap[error.type]?.includes(policyType)) {
            return false;
          }
        }
        const isAlert =
          error.code &&
          ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(errorClassificationLookup[Number(error.code)]);
        const handleAsAlert = error.handleAs === 'ALERT' || error.handleAs === 'BYPASS_ALERT';
        const includeAlert = codesArray.length === 0 || codesArray.includes(Number(error.code));
        return (isAlert || handleAsAlert) && includeAlert && error.handleAs !== 'REJECTION';
      });
    },
    getBlocks() {
      return self.errors.filter((error) => {
        const isBlock = !error.code || errorClassificationLookup[Number(error.code)] === 'BLOCK';
        const handleAsBlock = error.handleAs === 'BLOCK';

        return isBlock || handleAsBlock;
      });
    },
    getOfferDetails() {
      return { offer: self.offer, alerts: self.getAlerts(), blocks: self.getBlocks(), rejections: self.rejections };
    },
    cleanup() {
      if (subscription) {
        subscription.unsubscribe();
      }
      self.loading = false;
    }
  }))
  .views((self) => ({
    get rejections() {
      return self.errors.filter((err) => {
        // if error code is 10000 or greater, it's a rejection
        if (
          err.code &&
          err.code >= 10000 &&
          ((!err.handleAs && !errorClassificationLookup[err.code]) || err?.handleAs === 'REJECTION')
        ) {
          return true;
        }
        return false;
      });
    }
  }));
